import React, { useState, useEffect } from 'react';
import { Card, Button, Tabs, TabItem } from '../../ui';
import { useSalon } from '../../../contexts/SalonContext';
import { useToast } from '../../../components/ui/ToastManager';
import { useNavigate, useParams } from 'react-router-dom';
import { ClockIcon, MapPinIcon } from '@heroicons/react/24/outline';
import AddressAutocomplete from '../../shared/AddressAutocomplete';
import { updateSalonConfig } from '../../../firebase';
import { formatFullAddress } from '../../../utils/addressUtils';

// Horários padrão de funcionamento
const DEFAULT_BUSINESS_HOURS = {
  monday: { isOpen: true, intervals: [{ start: '09:00', end: '18:00' }] },
  tuesday: { isOpen: true, intervals: [{ start: '09:00', end: '18:00' }] },
  wednesday: { isOpen: true, intervals: [{ start: '09:00', end: '18:00' }] },
  thursday: { isOpen: true, intervals: [{ start: '09:00', end: '18:00' }] },
  friday: { isOpen: true, intervals: [{ start: '09:00', end: '18:00' }] },
  saturday: { isOpen: true, intervals: [{ start: '09:00', end: '14:00' }] },
  sunday: { isOpen: false, intervals: [{ start: '09:00', end: '18:00' }] }
};

// Componente para gerenciar horários de funcionamento
const BusinessHoursTab = ({ businessHours, onChange }) => {
  // Garantir que sempre temos uma estrutura válida para hours
  const [hours, setHours] = useState(() => {
    // Verificar se businessHours tem a estrutura esperada
    if (!businessHours || typeof businessHours !== 'object') {
      return {...DEFAULT_BUSINESS_HOURS};
    }
    
    // Garantir que todos os dias da semana estão presentes com estrutura adequada
    const validHours = {...DEFAULT_BUSINESS_HOURS};
    Object.keys(DEFAULT_BUSINESS_HOURS).forEach(day => {
      if (businessHours[day] && businessHours[day].isOpen !== undefined) {
        validHours[day] = {
          isOpen: !!businessHours[day].isOpen,
          intervals: Array.isArray(businessHours[day].intervals) && businessHours[day].intervals.length > 0
            ? [...businessHours[day].intervals]
            : [{ start: '09:00', end: '18:00' }]
        };
      }
    });
    
    return validHours;
  });
  
  const [showBreakTime, setShowBreakTime] = useState(false);
  
  const daysOfWeek = [
    { id: 'monday', label: 'Segunda-feira' },
    { id: 'tuesday', label: 'Terça-feira' },
    { id: 'wednesday', label: 'Quarta-feira' },
    { id: 'thursday', label: 'Quinta-feira' },
    { id: 'friday', label: 'Sexta-feira' },
    { id: 'saturday', label: 'Sábado' },
    { id: 'sunday', label: 'Domingo' }
  ];
  
  // Preparar opções de horário (24h, intervalos de 30min)
  const timeOptions = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      timeOptions.push(`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`);
    }
  }
  
  // Detectar se algum dia tem intervalo de almoço (múltiplos intervalos)
  useEffect(() => {
    try {
      const hasBreak = Object.values(hours).some(day => 
        day && day.isOpen && Array.isArray(day.intervals) && day.intervals.length > 1
      );
      setShowBreakTime(hasBreak);
    } catch (error) {
      console.error('Erro ao verificar intervalos:', error);
      setShowBreakTime(false);
    }
  }, [hours]);
  
  // Atualizar estado de abertura de um dia
  const handleToggleDay = (day) => {
    try {
      if (!hours[day]) return;
      
      const updatedHours = {
        ...hours,
        [day]: {
          ...hours[day],
          isOpen: !hours[day].isOpen
        }
      };
      
      setHours(updatedHours);
      onChange(updatedHours);
    } catch (error) {
      console.error(`Erro ao alterar estado do dia ${day}:`, error);
    }
  };
  
  // Atualizar um horário específico
  const handleTimeChange = (day, intervalIndex, field, value) => {
    try {
      if (!hours[day] || !Array.isArray(hours[day].intervals)) return;
      
      const updatedIntervals = [...hours[day].intervals];
      
      // Verificar se o índice é válido
      if (intervalIndex < 0 || intervalIndex >= updatedIntervals.length) return;
      
      updatedIntervals[intervalIndex] = {
        ...updatedIntervals[intervalIndex],
        [field]: value
      };
      
      const updatedHours = {
        ...hours,
        [day]: {
          ...hours[day],
          intervals: updatedIntervals
        }
      };
      
      setHours(updatedHours);
      onChange(updatedHours);
    } catch (error) {
      console.error(`Erro ao alterar horário do dia ${day}:`, error);
    }
  };
  
  // Adicionar um novo intervalo
  const handleAddInterval = (day) => {
    try {
      if (!hours[day] || !Array.isArray(hours[day].intervals)) return;
      
      const intervals = hours[day].intervals;
      const lastInterval = intervals[intervals.length - 1] || { start: '09:00', end: '18:00' };
      
      const newInterval = {
        start: lastInterval.end || '18:00',
        end: lastInterval.end || '18:00'
      };
      
      const updatedHours = {
        ...hours,
        [day]: {
          ...hours[day],
          intervals: [...intervals, newInterval]
        }
      };
      
      setHours(updatedHours);
      onChange(updatedHours);
    } catch (error) {
      console.error(`Erro ao adicionar intervalo no dia ${day}:`, error);
    }
  };
  
  // Remover um intervalo
  const handleRemoveInterval = (day, intervalIndex) => {
    try {
      if (!hours[day] || !Array.isArray(hours[day].intervals)) return;
      
      // Não permitir remover se for o único intervalo
      if (hours[day].intervals.length <= 1) return;
      
      // Verificar se o índice é válido
      if (intervalIndex < 0 || intervalIndex >= hours[day].intervals.length) return;
      
      const updatedIntervals = [...hours[day].intervals];
      updatedIntervals.splice(intervalIndex, 1);
      
      const updatedHours = {
        ...hours,
        [day]: {
          ...hours[day],
          intervals: updatedIntervals
        }
      };
      
      setHours(updatedHours);
      onChange(updatedHours);
    } catch (error) {
      console.error(`Erro ao remover intervalo no dia ${day}:`, error);
    }
  };
  
  // Aplicar configuração de um dia para todos os outros
  const handleApplyToAll = (sourceDayId) => {
    try {
      if (!hours[sourceDayId]) return;
      
      const sourceDay = hours[sourceDayId];
      const updatedHours = { ...hours };
      
      daysOfWeek.forEach(day => {
        if (day.id !== sourceDayId) {
          updatedHours[day.id] = { ...sourceDay };
        }
      });
      
      setHours(updatedHours);
      onChange(updatedHours);
    } catch (error) {
      console.error(`Erro ao aplicar configuração para todos os dias:`, error);
    }
  };
  
  // Ativar/desativar intervalo de almoço em todos os dias
  const toggleBreakTime = () => {
    try {
      const newShowBreakTime = !showBreakTime;
      setShowBreakTime(newShowBreakTime);
      
      const updatedHours = { ...hours };
      
      if (newShowBreakTime) {
        // Adicionar intervalo de almoço (12:00 - 13:00) para dias abertos
        Object.keys(updatedHours).forEach(dayId => {
          const day = updatedHours[dayId];
          if (day && day.isOpen && Array.isArray(day.intervals) && day.intervals.length === 1) {
            const currentInterval = day.intervals[0];
            
            // Verificar se o horário atual engloba o período de almoço
            if (currentInterval && currentInterval.start < "12:00" && currentInterval.end > "13:00") {
              updatedHours[dayId] = {
                ...day,
                intervals: [
                  { start: currentInterval.start, end: "12:00" },
                  { start: "13:00", end: currentInterval.end }
                ]
              };
            }
          }
        });
      } else {
        // Remover intervalo de almoço - juntar intervalos
        Object.keys(updatedHours).forEach(dayId => {
          const day = updatedHours[dayId];
          if (day && day.isOpen && Array.isArray(day.intervals) && day.intervals.length > 1) {
            const firstInterval = day.intervals[0] || { start: '09:00', end: '12:00' };
            const lastInterval = day.intervals[day.intervals.length - 1] || { start: '13:00', end: '18:00' };
            
            updatedHours[dayId] = {
              ...day,
              intervals: [
                { 
                  start: firstInterval.start || '09:00', 
                  end: lastInterval.end || '18:00' 
                }
              ]
            };
          }
        });
      }
      
      setHours(updatedHours);
      onChange(updatedHours);
    } catch (error) {
      console.error(`Erro ao alternar intervalo de almoço:`, error);
    }
  };
  
  return (
    <div className="animate-fadeIn">
      <h3 className="text-lg font-medium text-gray-800 mb-3">
        Horário de funcionamento
      </h3>
      <p className="text-gray-600 mb-6">
        Configure os dias e horários em que seu negócio estará aberto para agendamentos
      </p>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-8">
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h4 className="font-medium text-gray-800">Horário de atendimento</h4>
            <div className="flex items-center">
              <label htmlFor="breakTime" className="mr-2 text-sm text-gray-600">
                Intervalo para almoço (1 hora)
              </label>
              <div className="relative inline-block w-10 mr-2 align-middle select-none">
                <input
                  type="checkbox"
                  id="breakTime"
                  name="breakTime"
                  checked={showBreakTime}
                  onChange={toggleBreakTime}
                  className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                />
                <label
                  htmlFor="breakTime"
                  className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"
                ></label>
              </div>
            </div>
          </div>
        </div>

        <div className="divide-y divide-gray-100">
          {daysOfWeek.map((day) => (
            <div key={day.id} className="px-4 py-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id={`day-${day.id}`}
                    checked={hours[day.id]?.isOpen}
                    onChange={() => handleToggleDay(day.id)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor={`day-${day.id}`} className="ml-2 text-sm font-medium text-gray-700">
                    {day.label}
                  </label>
                </div>
                
                {day.id === 'monday' && (
                  <Button
                    type="button"
                    variant="outlined"
                    size="sm"
                    onClick={() => handleApplyToAll(day.id)}
                  >
                    Aplicar a todos
                  </Button>
                )}
              </div>
              
              {hours[day.id]?.isOpen && Array.isArray(hours[day.id]?.intervals) && (
                <div className="ml-6 mt-2">
                  {hours[day.id].intervals.map((interval, idx) => (
                    <div key={idx} className="flex items-center mb-2">
                      <select
                        className="mr-2 flex-1 py-1 px-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                        value={interval.start || '09:00'}
                        onChange={(e) => handleTimeChange(day.id, idx, 'start', e.target.value)}
                      >
                        {timeOptions.map(time => (
                          <option key={`start-${time}`} value={time}>{time}</option>
                        ))}
                      </select>
                      
                      <span className="mx-2 text-gray-500">até</span>
                      
                      <select
                        className="mr-2 flex-1 py-1 px-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                        value={interval.end || '18:00'}
                        onChange={(e) => handleTimeChange(day.id, idx, 'end', e.target.value)}
                      >
                        {timeOptions.map(time => (
                          <option key={`end-${time}`} value={time}>{time}</option>
                        ))}
                      </select>
                      
                      <div className="flex space-x-1">
                        {idx === hours[day.id].intervals.length - 1 && (
                          <button
                            type="button"
                            onClick={() => handleAddInterval(day.id)}
                            className="p-1 text-blue-600 hover:text-blue-800"
                          >
                            +
                          </button>
                        )}
                        
                        {hours[day.id].intervals.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveInterval(day.id, idx)}
                            className="p-1 text-red-600 hover:text-red-800"
                          >
                            -
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Componente para gerenciar endereço do salão
const AddressTab = ({ address, onChange }) => {
  // Garantir que temos um objeto válido para address
  const [currentAddress, setCurrentAddress] = useState(address || {});
  
  const handleAddressChange = (newAddress) => {
    try {
      if (!newAddress) return;
      
      setCurrentAddress(newAddress);
      onChange(newAddress);
    } catch (error) {
      console.error('Erro ao atualizar endereço:', error);
    }
  };
  
  return (
    <div className="animate-fadeIn">
      <h3 className="text-lg font-medium text-gray-800 mb-3">
        Endereço do salão
      </h3>
      <p className="text-gray-600 mb-6">
        Atualize o endereço que será exibido para seus clientes
      </p>
      
      <div className="mb-4">
        <AddressAutocomplete 
          initialData={currentAddress}
          onAddressChange={handleAddressChange}
        />
      </div>
    </div>
  );
};

/**
 * Componente principal para gerenciar configurações do salão
 */
const SalonSettings = () => {
  const { salonId } = useParams();
  const { salonConfig, loading: salonLoading } = useSalon();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('hours');
  const [formData, setFormData] = useState({
    businessHours: {...DEFAULT_BUSINESS_HOURS},
    address: {}
  });
  const [hoursDataReady, setHoursDataReady] = useState(false);
  
  // Inicializar formulário com dados do salão quando disponíveis
  useEffect(() => {
    if (salonConfig) {
      console.log('Dados do salão carregados:', salonConfig);
      
      // O salonConfig pode conter diretamente os campos de endereço
      // ou ter um objeto address com esses campos
      const address = salonConfig.address || {};
      
      // Se address está vazio mas temos campos de endereço no salonConfig, usá-los
      const addressData = Object.keys(address).length > 0 ? address : {
        cep: salonConfig.cep || '',
        street: salonConfig.street || '',
        number: salonConfig.number || '',
        complement: salonConfig.complement || '',
        neighborhood: salonConfig.neighborhood || '',
        city: salonConfig.city || '',
        state: salonConfig.state || '',
        formattedAddress: salonConfig.formattedAddress || salonConfig.address || ''
      };
      
      console.log('Endereço extraído:', addressData);
      
      setFormData({
        businessHours: salonConfig.businessHours || DEFAULT_BUSINESS_HOURS,
        address: addressData
      });
      setHoursDataReady(true);
    }
  }, [salonConfig]);
  
  const handleBusinessHoursChange = (newHours) => {
    try {
      setFormData(prev => ({
        ...prev,
        businessHours: newHours
      }));
    } catch (error) {
      console.error('Erro ao atualizar horários:', error);
    }
  };
  
  const handleAddressChange = (newAddress) => {
    try {
      // Adicionar campo formattedAddress para exibição
      const fullAddress = {
        ...newAddress,
        formattedAddress: formatFullAddress(newAddress)
      };
      
      setFormData(prev => ({
        ...prev,
        address: fullAddress
      }));
    } catch (error) {
      console.error('Erro ao atualizar endereço:', error);
    }
  };
  
  const handleSave = async () => {
    if (!salonId) {
      showToast('ID do salão não encontrado', 'error');
      return;
    }
    
    setLoading(true);
    
    try {
      await updateSalonConfig(salonId, formData);
      showToast('Configurações do salão atualizadas com sucesso', 'success');
    } catch (error) {
      console.error('Erro ao atualizar configurações do salão:', error);
      showToast('Erro ao atualizar configurações. Tente novamente.', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  if (salonLoading) {
    return (
      <div className="p-4 md:p-6 flex justify-center items-center">
        <div className="animate-pulse text-center">
          <p className="text-gray-500">Carregando dados do salão...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-4 md:p-6">
      <h2 className="text-2xl font-semibold mb-6">Configurações do Salão</h2>
      
      <Card className="mb-6">
        <Tabs value={activeTab} onChange={setActiveTab}>
          <TabItem 
            value="hours" 
            label="Horários" 
            icon={<ClockIcon className="h-5 w-5" />}
          />
          <TabItem 
            value="address" 
            label="Endereço" 
            icon={<MapPinIcon className="h-5 w-5" />}
          />
        </Tabs>
        
        <div className="mt-6 p-4">
          {activeTab === 'hours' && hoursDataReady && (
            <BusinessHoursTab 
              businessHours={formData.businessHours}
              onChange={handleBusinessHoursChange}
            />
          )}
          
          {activeTab === 'address' && (
            <AddressTab 
              address={formData.address}
              onChange={handleAddressChange}
            />
          )}
        </div>
      </Card>
      
      <div className="flex justify-end mt-4">
        <Button 
          variant="outlined" 
          onClick={() => navigate(-1)}
          className="mr-2"
        >
          Cancelar
        </Button>
        <Button 
          onClick={handleSave}
          loading={loading}
        >
          Salvar Alterações
        </Button>
      </div>
    </div>
  );
};

export default SalonSettings; 